import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  combineLatestWith,
  debounceTime,
  exhaustMap,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { StateService } from 'src/app/auth/state.service';
import { DataRequestState } from 'src/app/data-request/model';
import { toRequestState } from 'src/app/data-request/operators';
import { ApiOrganization } from 'src/app/models_new/classes/api-models/ApiOrganization';
import {
  ApiPattern,
  IApiPattern,
} from 'src/app/models_new/classes/api-models/ApiPattern';
import {
  ApiScene,
  IApiScene,
} from 'src/app/models_new/classes/api-models/ApiScene';
import {
  ApiStrategy,
  IApiStrategy,
} from 'src/app/models_new/classes/api-models/ApiStrategy';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { DialogSize } from 'src/app/models_new/enums/dialogSize';
import { LocalStorageKey } from 'src/app/models_new/enums/local-storage-keys';
import { SimulationStatus } from 'src/app/models_new/enums/simulation-status';
import { HardwareApiService } from 'src/app/services/api/hardware-api.service';
import { PatternApiService } from 'src/app/services/api/pattern-api.service';
import {
  SimulationApiService,
  SimulationInfo,
} from 'src/app/services/api/simulation-api.service';
import { SoftwareApiService } from 'src/app/services/api/software-api.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { NewSimpleSimulationComponent } from '../../dialogs/new-simple-simulation/new-simple-simulation.component';
import { IZimWizardState, ZimWizard } from './zim-wizard';
import { NotificationService } from 'src/app/services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class SimulationWizardDataService {
  patterns$: Observable<IApiPattern[]>;
  hardwares$: Observable<IApiScene[]>;
  softwares$: Observable<IApiStrategy[]>;
  softwarePresets$: Observable<IApiStrategy[]>;
  init$: Observable<DataRequestState<boolean>>;
  zimWizard: ZimWizard;
  newSummaryInit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  destroy$ = new Subject<undefined>();

  constructor(
    private localStorageService: LocalStorageService,
    private patternApi: PatternApiService,
    private hwApi: HardwareApiService,
    private swApi: SoftwareApiService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private simulationApi: SimulationApiService,
    private errorHandler: ErrorHandlerService,
    private stateService: StateService,
    private notify: NotificationService
  ) {
    this.patterns$ = this.stateService.getCustomerOrSalesOrganization().pipe(
      map((org: ApiOrganization) => org.id),
      switchMap((orgId: string) => this.patternApi.fetchSimWizPatterns(orgId)),
      takeUntil(this.destroy$)
    );

    this.hardwares$ = this.stateService.getCustomerOrSalesOrganization().pipe(
      map((org: ApiOrganization) => org.id),
      switchMap((orgId: string) =>
        combineLatest([
          this.hwApi.fetchSimWizSalesOrgsHardwares(orgId),
          this.hwApi.fetchSimWizHardwares(orgId),
        ]).pipe(map(([customer_hw, sales_hw]) => sales_hw.concat(customer_hw)))
      ),
      takeUntil(this.destroy$)
    );

    this.softwares$ = this.stateService.getCustomerOrSalesOrganization().pipe(
      map((org: ApiOrganization) => org.id),
      switchMap((orgId: string) =>
        combineLatest([
          this.swApi.fetchSimWizSalesOrgsStrategies(orgId),
          this.swApi.fetchSimWizStrategies(orgId),
        ]).pipe(map(([customer_sw, sales_sw]) => sales_sw.concat(customer_sw)))
      ),
      takeUntil(this.destroy$)
    );

    this.softwarePresets$ = this.stateService
      .getCustomerOrSalesOrganization()
      .pipe(
        map((org: ApiOrganization) => org.id),
        switchMap((_: string) => this.swApi.fetchDefaultStrategies()),
        takeUntil(this.destroy$)
      );

    this.init$ = combineLatest([
      this.patterns$,
      this.hardwares$,
      this.softwares$,
      this.softwarePresets$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([p, hw, sw, swp]) => {
        return p && hw && sw && swp ? true : false;
      }),
      tap((v) => {
        if (v) {
          const saved: IZimWizardState = this.localStorageService.getData(
            LocalStorageKey.SIMULATION_WIZARD_STATE
          );

          this.zimWizard = new ZimWizard(
            this.route,
            this.router,
            this.localStorageService,
            saved?.patterns || [],
            saved?.hardwares || [],
            saved?.softwares || [],
            saved?.softwarePresets || [],
            saved?.dualProductMode || false
          );
        }
      }),
      toRequestState(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  destroy() {
    this.newSummaryInit.next(false);
    this.localStorageService.removeData(
      LocalStorageKey.SIMULATION_WIZARD_STATE
    );
    this.zimWizard.clearData();
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  closeWizard() {
    this.destroy();
    this.router.navigate([pagesPATH.SIMULATIONS]);
  }

  getSimulationInfo(
    name: string,
    nr: number,
    hw: ApiScene,
    sw: ApiStrategy,
    pattern: ApiPattern,
    secondary_pattern?: ApiPattern
  ): SimulationInfo {
    const isDualProduct = secondary_pattern !== undefined;
    return {
      name: isDualProduct
        ? `${name} - ${hw.name} - ${nr} - Dual product`
        : `${name} - ${pattern.name} - ${nr}`,
      pattern_id: pattern.id,
      second_pattern_id: secondary_pattern?.id ?? null,
      scene_id: hw.id,
      strategy_id: sw.id,
      simulation_state: SimulationStatus.QUEUED,
      metadata: {
        unitSystem:
          this.localStorageService.getData(
            LocalStorageKey.PREFERRED_UNIT_SYSTEM
          ) || 'metric',
      },
    } as SimulationInfo;
  }

  startSimulation() {
    const dialogRef = this.dialogService.showCustomDialog(
      NewSimpleSimulationComponent,
      DialogSize.SMALL,
      null,
      {
        simulation: {
          name: '',
        },
      }
    );

    this.stateService
      .getCustomerOrSalesOrganization()
      .pipe(
        take(1),
        map((org: ApiOrganization) => org.id),
        /**
         * Preventive use of `debounceTime`, `take(1)`, and `exhaustMap` to avoid duplicate batch insertions
         * potentially caused by repeated dialog submissions or UI race conditions.
         * The issue has been reported multiple times, but remains unreproducible in a controlled environment.
         *
         * Learn more {@link https://rocketfarm.atlassian.net/browse/PALLY-4148 | here}
         */
        combineLatestWith(
          dialogRef.afterClosed().pipe(debounceTime(500), take(1))
        ),
        exhaustMap(([orgId, sim_name]) => {
          if (!sim_name) {
            throw 'cancel';
          }
          if (sim_name[0].name && !sim_name[0].value) {
            throw new Error('Simulation name is required!');
          }
          const simulations: SimulationInfo[] = [];

          // Normal simulation
          if (!this.zimWizard.dualProductMode.value) {
            for (let pattern of this.zimWizard.patterns) {
              for (let scene of this.zimWizard.hardwares) {
                for (let strategy of this.zimWizard.softwares.concat(
                  this.zimWizard.softwarePresets
                )) {
                  simulations.push(
                    this.getSimulationInfo(
                      sim_name[0].value,
                      simulations.length + 1,
                      scene,
                      strategy,
                      pattern
                      // NOTE: Normal simulation, omitting secondary pattern.
                    )
                  );
                }
              }
            }

            // Dual product simulation
          } else {
            for (let scene of this.zimWizard.hardwares) {
              for (let strategy of this.zimWizard.softwares.concat(
                this.zimWizard.softwarePresets
              )) {
                simulations.push(
                  this.getSimulationInfo(
                    sim_name[0].value,
                    simulations.length + 1,
                    scene,
                    strategy,
                    this.zimWizard.patterns[0],
                    // NOTE: Dual product simulation, using both patterns.
                    this.zimWizard.patterns[1]
                  )
                );
              }
            }
          }
          this.notify.showMessage('Creating simulation');
          return this.simulationApi.insertSimulationBatch(simulations, orgId);
        })
      )
      .subscribe({
        next: () => {
          this.destroy();
          this.router.navigate([pagesPATH.SIMULATIONS]);
        },
        error: (err) => {
          if (err !== 'cancel') {
            this.errorHandler.handleError(err);
          }
        },
      });
  }
}
